import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { toastError } from '../../shared/toastHelper';
import OrganizationFormModal from '../Organizations/components/OrganizationFormModal';

const DEBOUNCE_MS = 250;

const wrapperStyle = { position: 'relative', width: 240 };
const menuStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 100,
  margin: '4px 0 0',
  padding: 4,
  listStyle: 'none',
  background: 'var(--sl-bg-primary, #fff)',
  borderRadius: 8,
  boxShadow: '0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)',
  maxHeight: 240,
  overflowY: 'auto',
};
const optionStyle = (isActive) => ({
  padding: '8px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  background: isActive ? 'var(--sl-bg-active, #f4f9fc)' : 'transparent',
});

const OrganizationAutocomplete = ({
  teamId, onPick, onCancel, autoFocus, placeholder, initialQuery,
  team, csrfToken, customFieldDefinitions,
}) => {
  const [query, setQuery] = useState(initialQuery || '');
  const [results, setResults] = useState([]);
  const [highlighted, setHighlighted] = useState(0);
  const [loading, setLoading] = useState(false);
  const [createModalName, setCreateModalName] = useState(null);

  const hasRequiredCustomFields = customFieldDefinitions.some(d => d.required);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [autoFocus]);

  const runSearch = useCallback((value) => {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const myRequestId = ++requestIdRef.current;
    axios.get(`/teams/${teamId}/crm/organizations/search`, { params: { q: trimmed } })
      .then(({ data }) => {
        if (myRequestId !== requestIdRef.current) return;
        setResults(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (myRequestId !== requestIdRef.current) return;
        setResults([]);
      })
      .finally(() => {
        if (myRequestId !== requestIdRef.current) return;
        setLoading(false);
      });
  }, [teamId]);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setHighlighted(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(value), DEBOUNCE_MS);
  };

  const hasExactMatch = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return false;
    return results.some(r => (r.name || '').toLowerCase() === needle);
  }, [results, query]);

  const showCreateRow = query.trim().length > 0 && !hasExactMatch;

  const options = useMemo(() => {
    const opts = results.map(r => ({ kind: 'organization', organization: r }));
    if (showCreateRow) opts.push({ kind: 'create', name: query.trim() });
    return opts;
  }, [results, showCreateRow, query]);

  const commitOption = useCallback((opt) => {
    if (!opt) return;
    if (opt.kind === 'create') {
      if (hasRequiredCustomFields) {
        setCreateModalName(opt.name);
        return;
      }
      axios.post(`/teams/${teamId}/crm/organizations`, { organization: { name: opt.name } })
        .then(({ data }) => onPick({ organizationId: data.id, organizationName: data.name, organizationAvatarUrl: data.avatar_thumb_url }))
        .catch((error) => {
          toastError(error.response?.data?.join(', ') || 'Failed to create organization');
        });
    } else if (opt.kind === 'organization') {
      onPick({ organizationId: opt.organization.id, organizationName: opt.organization.name, organizationAvatarUrl: opt.organization.avatar_thumb_url });
    }
  }, [onPick, teamId, hasRequiredCustomFields]);

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setResults([]);
      onCancel();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      commitOption(options[highlighted]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted(h => Math.min(h + 1, Math.max(options.length - 1, 0)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted(h => Math.max(h - 1, 0));
    }
  };

  const modalOpen = createModalName !== null;
  const showDropdown = !modalOpen && query.trim().length > 0 && (options.length > 0 || loading);

  return (
    <div style={wrapperStyle}>
      <input
        ref={inputRef}
        type="text"
        className="form-control"
        value={query}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (!modalOpen) onCancel(); }}
        autoComplete="off"
        data-testid="organization-autocomplete-input"
      />
      {showDropdown && (
        <ul style={menuStyle} role="listbox">
          {options.map((opt, idx) => {
            const isActive = idx === highlighted;
            if (opt.kind === 'organization') {
              return (
                <li
                  key={`organization-${opt.organization.id}`}
                  role="option"
                  aria-selected={isActive}
                  style={optionStyle(isActive)}
                  onMouseDown={(e) => { e.preventDefault(); commitOption(opt); }}
                  onMouseEnter={() => setHighlighted(idx)}
                >
                  {opt.organization.name}
                </li>
              );
            }
            return (
              <li
                key="create-new"
                role="option"
                aria-selected={isActive}
                style={{ ...optionStyle(isActive), fontWeight: 600 }}
                onMouseDown={(e) => { e.preventDefault(); commitOption(opt); }}
                onMouseEnter={() => setHighlighted(idx)}
                data-testid="organization-autocomplete-create-row"
              >
                + Create &quot;{opt.name}&quot;
              </li>
            );
          })}
          {loading && options.length === 0 && (
            <li style={optionStyle(false)}>Searching…</li>
          )}
        </ul>
      )}
      {modalOpen && (
        <OrganizationFormModal
          isOpen
          team={team}
          csrfToken={csrfToken}
          customFieldDefinitions={customFieldDefinitions}
          organization={{ name: createModalName }}
          onClose={() => { setCreateModalName(null); onCancel(); }}
          onSaved={(saved) => {
            setCreateModalName(null);
            onPick({ organizationId: saved.id, organizationName: saved.name, organizationAvatarUrl: saved.avatar_thumb_url });
          }}
        />
      )}
    </div>
  );
};

OrganizationAutocomplete.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onPick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  initialQuery: PropTypes.string,
  team: PropTypes.object,
  csrfToken: PropTypes.string,
  customFieldDefinitions: PropTypes.array,
};

OrganizationAutocomplete.defaultProps = {
  autoFocus: true,
  placeholder: 'Search organizations…',
  initialQuery: '',
  team: null,
  csrfToken: null,
  customFieldDefinitions: [],
};

export default OrganizationAutocomplete;
